import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { PageHead, Skel, Stat, Empty } from '../components/ui'
import { money, thaiDate } from '../lib/format'

// Board oversight channel (part 184). Aggregates only — the board governs, it does not investigate.
type Severity = { severity: string; count: number; amount_satang: number }
type Ageing = { bucket: string; count: number }
type Trend = { month: string; findings: number; amount_satang: number }
type TopRule = { rule_code: string; title: string; count: number; amount_satang: number }
type Summary = {
  generated_at: string
  findings: number
  open_findings: number
  amount_at_risk_satang: number
  engagements: number
  documents: number
  documents_extracted: number
  open_cases: number
  severity: Severity[]
  case_ageing: Ageing[] | null
  trend: Trend[]
  top_rules: TopRule[]
}

const sevLabel: Record<string, string> = { critical: 'วิกฤต', high: 'สูง', medium: 'กลาง', low: 'ต่ำ' }
const monthLabel = (m: string) => {
  const [y, mm] = m.split('-')
  return `${['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'][Number(mm) - 1]} ${String(Number(y) + 543).slice(2)}`
}

export function Board() {
  const q = useQuery({
    queryKey: ['board'],
    queryFn: () => api<Summary>('/v1/board/summary'),
    staleTime: 60_000,
  })
  const s = q.data
  const maxTrend = Math.max(1, ...(s?.trend ?? []).map((t) => t.findings))
  const maxAge = Math.max(1, ...(s?.case_ageing ?? []).map((a) => a.count))

  return (
    <>
      <PageHead title="ภาพรวมคณะกรรมการ" sub="ข้อมูลรวมระดับองค์กร ไม่มีรายละเอียดระดับรายการ · ปรับปรุงเมื่อเปิดหน้า" />

      {q.isLoading || !s ? (
        <div className="stack"><Skel h={120} /><Skel h={200} /></div>
      ) : (
        <>
          <div className="grid-4 stagger">
            <Stat label="มูลค่าความเสี่ยงสะสม" value={money(s.amount_at_risk_satang, { compact: true })}
              foot={`จากข้อสังเกต ${s.findings} รายการ`} />
            <Stat label="ข้อสังเกตที่ยังเปิด" value={s.open_findings} unit="รายการ"
              foot={`ปิดแล้ว ${s.findings - s.open_findings} รายการ`} />
            <Stat label="เคสที่อยู่ระหว่างดำเนินการ" value={s.open_cases} unit="เคส"
              foot="รวมทุกสถานะที่ยังไม่ปิด" />
            <Stat label="เอกสารที่ตรวจแล้ว" value={s.documents_extracted} unit={`จาก ${s.documents}`}
              foot={`ครอบคลุม ${s.engagements} โครงการ`} />
          </div>

          <div className="grid-main" style={{ marginTop: 18 }}>
            <div className="card card-pad">
              <div className="card-label">ความเสี่ยงตามระดับ</div>
              <table className="table" style={{ marginTop: 12 }}>
                <thead><tr><th>ระดับ</th><th className="num">จำนวน</th><th className="num">มูลค่า (บาท)</th></tr></thead>
                <tbody>
                  {s.severity.length === 0 && <tr><td colSpan={3}><span className="dim">ยังไม่มีข้อสังเกต</span></td></tr>}
                  {s.severity.map((row) => (
                    <tr key={row.severity}>
                      <td>{sevLabel[row.severity] ?? row.severity}</td>
                      <td className="num">{row.count}</td>
                      <td className="num">{money(row.amount_satang, { compact: true })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card card-pad">
              <div className="card-label">อายุเคสที่ยังเปิด</div>
              <div className="stack" style={{ marginTop: 14, gap: 10 }}>
                {(s.case_ageing ?? []).length === 0 && <span className="dim">ไม่มีเคสค้าง</span>}
                {(s.case_ageing ?? []).map((a) => (
                  <div key={a.bucket} className="row" style={{ gap: 12 }}>
                    <span style={{ width: 84, fontSize: 12.5 }}>{a.bucket}</span>
                    <span className="bar" style={{ flex: 1 }}><i style={{ width: `${(a.count / maxAge) * 100}%` }} /></span>
                    <span className="num" style={{ width: 28, textAlign: 'right' }}>{a.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 18 }}>
            <div className="card-label">แนวโน้ม 6 เดือน</div>
            {s.trend.length === 0 ? (
              <div style={{ marginTop: 12 }}><Empty title="ยังไม่มีข้อมูลย้อนหลัง" body="ข้อมูลจะเริ่มสะสมเมื่อมีการตรวจรอบแรก" /></div>
            ) : (
              <div className="row" style={{ alignItems: 'flex-end', gap: 18, height: 120, marginTop: 16 }}>
                {s.trend.map((t) => (
                  <div key={t.month} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ height: 72, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                      {t.findings > 0 && <div title={`${t.findings} รายการ`}
                        style={{ width: 26, height: `${Math.max(6, (t.findings / maxTrend) * 72)}px`, background: 'var(--color-gold)', opacity: 0.75, borderRadius: 2 }} />}
                    </div>
                    <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>{monthLabel(t.month)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card card-pad" style={{ marginTop: 18 }}>
            <div className="card-label">กฎที่พบมากที่สุด</div>
            <table className="table" style={{ marginTop: 12 }}>
              <thead><tr><th>กฎ</th><th className="num">ครั้ง</th><th className="num">มูลค่า (บาท)</th></tr></thead>
              <tbody>
                {s.top_rules.map((r) => (
                  <tr key={r.rule_code}>
                    <td>{r.title}<div className="dim" style={{ fontSize: 11 }}>{r.rule_code}</div></td>
                    <td className="num">{r.count}</td>
                    <td className="num">{money(r.amount_satang, { compact: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="dim" style={{ marginTop: 14, fontSize: 11.5 }}>
            สร้างเมื่อ {thaiDate(s.generated_at)} · ตัวเลขเป็นข้อมูลรวม ไม่สามารถใช้ระบุตัวบุคคลหรือรายการได้
          </div>
        </>
      )}
    </>
  )
}
